export const LOGIN_TENANT_SELECTION_STATUS = 300;

export interface LoginCredentials {
    email: string;
    password: string;
    tenantSlug?: string | null;
}

export interface LoginTenantChoice {
    slug: string;
    name: string;
}

export interface LoginUser {
    id: string;
    email: string;
    name?: string | null;
    role: string;
    tenantId: string;
    // Optional only for rolling compatibility with a backend that predates the
    // signed rotation claim. Absence is UNKNOWN at boot, never equivalent to false.
    mustChangePassword?: boolean;
}

export interface LoginSuccessPayload {
    accessToken: string;
    refreshToken?: string | null;
    expiresIn?: number;
    usingBootstrapPassword?: boolean;
    user: LoginUser;
}

export interface LoginWorkspaceBrand {
    name: string;
    primaryColor: string | null;
}

export type LoginWorkspaceBrandResult =
    | { kind: 'matched'; workspace: LoginWorkspaceBrand }
    | { kind: 'ambiguous' }
    | { kind: 'unknown' }
    | { kind: 'unavailable' };

export type LoginRequestResult =
    | { kind: 'success'; payload: LoginSuccessPayload }
    | { kind: 'tenant-selection'; message: string; choices: LoginTenantChoice[] }
    | { kind: 'error'; status: number; message: string; retryAfterSeconds?: number; serverUnavailable?: boolean };

/**
 * The login request reached no QuoteMaker server: a gateway answered 502/503/
 * 504 (a deploy or restart), the sign-in service was down, or nothing answered
 * at all. The credentials were never judged. Outage lane, 2026-09-29: this used
 * to fall through to "Invalid email or password".
 */
export const LOGIN_SERVER_UNAVAILABLE_MESSAGE = "QuoteMaker is updating or can't reach its server. We'll sign you in as soon as it answers.";

function isLoginServerUnavailableStatus(status: number): boolean {
    return status === 502 || status === 503 || status === 504;
}

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

const WORKSPACE_DOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
const SIX_DIGIT_HEX_RE = /^#[0-9a-f]{6}$/i;

function text(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function isWorkspaceDomain(value: string): boolean {
    if (value.length < 4 || value.length > 253 || !WORKSPACE_DOMAIN_RE.test(value)) return false;
    const labels = value.split('.');
    const topLevel = labels[labels.length - 1] || '';
    return labels.every((label) => label.length <= 63)
        && topLevel.length >= 2
        && /^[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(topLevel);
}

/**
 * Return the public company-domain portion of a complete work email.
 *
 * The login preview sends only this domain to the backend. The address local
 * part is not needed for visual branding and must not become an account lookup.
 */
export function workspaceDomainFromEmail(value: unknown): string | null {
    const email = text(value).toLowerCase();
    if (!email || /\s/.test(email)) return null;
    const at = email.lastIndexOf('@');
    if (at <= 0 || at !== email.indexOf('@') || at === email.length - 1) return null;
    const domain = email.slice(at + 1).replace(/\.$/, '');
    if (!isWorkspaceDomain(domain)) return null;
    return domain;
}

function normaliseWorkspaceBrand(value: unknown): LoginWorkspaceBrand | null {
    if (!value || typeof value !== 'object') return null;
    const record = value as Record<string, unknown>;
    const name = text(record.name);
    if (!name || name.length > 255) return null;

    const colourText = record.primaryColor == null ? '' : text(record.primaryColor);
    if (colourText && !SIX_DIGIT_HEX_RE.test(colourText)) return null;

    return {
        name,
        primaryColor: colourText ? colourText.toUpperCase() : null,
    };
}

/**
 * Resolve optional public login branding from a company domain.
 *
 * This is presentation only. It never returns or accepts a tenant id/slug and
 * it does not influence requestQuoteMakerLogin(). The password-verified login
 * route remains the sole authority for workspace access.
 */
export async function requestLoginWorkspaceBrand(
    domain: string,
    {
        signal,
        fetchImpl = fetch,
    }: { signal?: AbortSignal; fetchImpl?: FetchLike } = {},
): Promise<LoginWorkspaceBrandResult> {
    const normalisedDomain = text(domain).toLowerCase().replace(/\.$/, '');
    if (!isWorkspaceDomain(normalisedDomain)) {
        throw new TypeError('Invalid work email domain');
    }

    const response = await fetchImpl('/api/auth/workspace-brand', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: normalisedDomain }),
        signal,
    });
    const data: unknown = await response.json().catch(() => ({}));
    const record = data && typeof data === 'object' ? data as Record<string, unknown> : {};

    if (response.status === 503 || record.kind === 'unavailable') return { kind: 'unavailable' };
    if (!response.ok) return { kind: 'unavailable' };
    if (record.kind === 'unknown') return { kind: 'unknown' };
    if (record.kind === 'ambiguous') return { kind: 'ambiguous' };
    if (record.kind !== 'matched') return { kind: 'unavailable' };

    const workspace = normaliseWorkspaceBrand(record.workspace);
    return workspace ? { kind: 'matched', workspace } : { kind: 'unavailable' };
}

function normaliseTenantSlug(value: unknown): string | null {
    const slug = text(value).toLowerCase();
    return slug.length > 0 && slug.length <= 100 ? slug : null;
}

function normaliseTenantChoices(value: unknown): LoginTenantChoice[] {
    if (!Array.isArray(value)) return [];

    const seen = new Set<string>();
    const choices: LoginTenantChoice[] = [];
    for (const candidate of value) {
        if (!candidate || typeof candidate !== 'object') continue;
        const record = candidate as Record<string, unknown>;
        const slug = normaliseTenantSlug(record.slug);
        const name = text(record.name);
        if (!slug || !name || name.length > 255 || seen.has(slug)) continue;
        seen.add(slug);
        choices.push({ slug, name });
    }
    return choices;
}

function normaliseLoginRetryAfterSeconds(response: Response, record: Record<string, unknown>): number | null {
    const candidates = [record.retryAfterSeconds, response.headers?.get?.('Retry-After')];
    const valid = candidates
        .map((candidate) => Number(candidate))
        // The current API is capped at five minutes. Accept an older server's
        // longer numeric Retry-After during a rolling update so the UI never
        // re-enables before that server will accept the next attempt.
        .filter((seconds) => Number.isFinite(seconds) && seconds >= 1 && seconds <= 3_600)
        .map((seconds) => Math.ceil(seconds));
    return valid.length > 0 ? Math.max(...valid) : null;
}

function isLoginSuccessPayload(value: unknown): value is LoginSuccessPayload {
    if (!value || typeof value !== 'object') return false;
    const payload = value as Record<string, unknown>;
    if (!payload.user || typeof payload.user !== 'object') return false;
    const user = payload.user as Record<string, unknown>;
    return text(payload.accessToken).length > 0
        && text(user.id).length > 0
        && text(user.email).length > 0
        && text(user.role).length > 0
        && text(user.tenantId).length > 0;
}

/**
 * Submit QuoteMaker credentials without performing email-only tenant discovery.
 *
 * A company list is accepted only from the login route's HTTP 300 response,
 * which the backend emits after the password has verified against more than one
 * active tenant-local account. The request never sends a tenant id. Selecting a
 * company repeats the same credential check with the server-issued tenant slug;
 * the backend still derives the JWT tenant id from the matched users row.
 */
export async function requestQuoteMakerLogin(
    credentials: LoginCredentials,
    { fetchImpl = fetch }: { fetchImpl?: FetchLike } = {},
): Promise<LoginRequestResult> {
    const email = text(credentials.email);
    const password = typeof credentials.password === 'string' ? credentials.password : '';
    const requestedTenantSlug = credentials.tenantSlug == null
        ? null
        : normaliseTenantSlug(credentials.tenantSlug);

    if (credentials.tenantSlug != null && !requestedTenantSlug) {
        throw new TypeError('Invalid company selection');
    }

    const body: { email: string; password: string; tenantSlug?: string } = { email, password };
    if (requestedTenantSlug) body.tenantSlug = requestedTenantSlug;

    let response: Response;
    try {
        response = await fetchImpl('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') throw err;
        // No HTTP response at all: nothing judged the credentials.
        return { kind: 'error', status: 0, message: LOGIN_SERVER_UNAVAILABLE_MESSAGE, serverUnavailable: true };
    }
    const data: unknown = await response.json().catch(() => ({}));

    if (response.status === LOGIN_TENANT_SELECTION_STATUS) {
        const record = data && typeof data === 'object' ? data as Record<string, unknown> : {};
        const choices = normaliseTenantChoices(record.chooseTenant);
        if (choices.length >= 2) {
            return {
                kind: 'tenant-selection',
                message: text(record.error) || 'Choose the company you want to open.',
                choices,
            };
        }
        return {
            kind: 'error',
            status: response.status,
            message: 'QuoteMaker could not determine your company. Sign in again.',
        };
    }

    if (!response.ok) {
        const record = data && typeof data === 'object' ? data as Record<string, unknown> : {};
        if (isLoginServerUnavailableStatus(response.status)) {
            return {
                kind: 'error',
                status: response.status,
                message: LOGIN_SERVER_UNAVAILABLE_MESSAGE,
                serverUnavailable: true,
            };
        }
        const retryAfterSeconds = response.status === 429
            ? normaliseLoginRetryAfterSeconds(response, record)
            : null;
        return {
            kind: 'error',
            status: response.status,
            message: text(record.error) || 'Invalid email or password',
            ...(retryAfterSeconds ? { retryAfterSeconds } : {}),
        };
    }

    if (!isLoginSuccessPayload(data)) {
        return {
            kind: 'error',
            status: response.status,
            message: 'QuoteMaker returned an invalid sign-in response. Sign in again.',
        };
    }

    return { kind: 'success', payload: data };
}
